import { useInfiniteQuery, InfiniteData } from "@tanstack/react-query"
import { useAxios } from "../../contexts/AxiosContext"
import { ExperimentStatus } from "@prismancy/shared"
import { Experiment } from "../../types"
import useWorkspace from "../../components/hooks/workspace/useWorkspace"
import { queryKeys } from "../../queryKeys"

type UseExperimentsType = {
  experiments: Experiment[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  error: Error | null
  totalCount: number
}

type PaginationInfo = {
  limit: number
  nextCursor: string | null
  hasMore: boolean
  totalCount: number
}

type ExperimentsResponse = {
  status: number
  experiments: Experiment[]
  pagination: PaginationInfo
}

const useExperiments = (status: ExperimentStatus, limit: number = 20): UseExperimentsType => {

  const { axiosApiInstance } = useAxios();
  const { workspace } = useWorkspace()
  const workspaceId = workspace?.id

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error
  } = useInfiniteQuery<ExperimentsResponse, Error, InfiniteData<ExperimentsResponse>, ReturnType<typeof queryKeys.experiments.list>, string | undefined>({
    queryKey: queryKeys.experiments.list(workspaceId, status, limit),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }): Promise<ExperimentsResponse> => {
      if (!workspaceId) return { status: 200, experiments: [], pagination: { limit, nextCursor: null, hasMore: false, totalCount: 0 } };

      const params: Record<string, string | number> = {
        limit
      };
      if (status) params.status = status;
      if (pageParam) params.cursor = pageParam;

      const response = await axiosApiInstance.get<ExperimentsResponse>(
        `/experiment/workspace-experiments/${workspaceId}/paginated`,
        { params }
      );

      return response.data
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined
    },
    placeholderData: undefined,
    enabled: Boolean(workspaceId),
  });

  const allExperiments = data?.pages.flatMap((page: ExperimentsResponse) =>
    page.experiments.map((experiment: Experiment) => {
      const normalizedStatus = String(experiment.status || '').toLowerCase() as ExperimentStatus
      return {
        ...experiment,
        status: normalizedStatus,
        objective_id: Number(experiment.objective_id || 0),
        traffic_allocation: Number(experiment.traffic_allocation || 0)
      }
    })
  ) ?? [];


  // Deduplicate experiments by id
  const experimentsMap = new Map<string, Experiment>();
  allExperiments.forEach((experiment: Experiment) => {
    if (!experimentsMap.has(experiment.id)) {
      experimentsMap.set(experiment.id, experiment);
    }
  });
  const experiments = Array.from(experimentsMap.values());

  const totalCount = data?.pages[0]?.pagination?.totalCount ?? 0;

  return {
    experiments,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    error,
    totalCount
  };
};

export default useExperiments
